import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Spinner,
  Envelope,
  Lock
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AffiliateLoginPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [statusEmail, setStatusEmail] = useState('');
  const [showStatusCheck, setShowStatusCheck] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/affiliate/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Store affiliate token
      localStorage.setItem('affiliate_token', data.token);
      localStorage.setItem('affiliate_data', JSON.stringify(data.affiliate));
      
      toast.success('Login successful!');
      navigate('/affiliate/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckStatus = async (e) => {
    e.preventDefault();
    setCheckingStatus(true);

    try {
      const response = await fetch(`${API_URL}/api/affiliate/application-status?email=${encodeURIComponent(statusEmail)}`);
      const data = await response.json();

      if (data.status === 'not_found') {
        toast.error('No application found for this email');
      } else if (data.status === 'pending') {
        toast.info('Your application is under review');
      } else if (data.status === 'approved') {
        toast.success('Your application has been approved! Check your email for login credentials.');
      } else if (data.status === 'rejected') {
        toast.error('Your application was not approved');
      }
    } catch (error) {
      toast.error('Failed to check status');
    } finally {
      setCheckingStatus(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        {/* Back Button */}
        <button
          onClick={() => navigate('/affiliate')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Affiliate Program
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-serif text-white mb-2">Affiliate Login</h1>
          <p className="text-zinc-400">
            Access your affiliate dashboard
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="space-y-6">
          <div className="bg-[#121212] border border-white/10 p-8 space-y-6">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Envelope size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-[#0a0a0a] border-white/10 text-white pl-10"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="bg-[#0a0a0a] border-white/10 text-white pl-10"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#D4AF37] text-black hover:bg-[#C4A030] py-5 font-semibold"
              data-testid="affiliate-login-submit"
            >
              {loading ? (
                <>
                  <Spinner size={20} className="animate-spin mr-2" />
                  Logging in...
                </>
              ) : (
                'Log In'
              )}
            </Button>
          </div>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-4 my-8">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-zinc-500 text-sm">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        {/* Check Application Status */}
        {!showStatusCheck ? (
          <div className="text-center space-y-4">
            <p className="text-zinc-400">Don't have an account yet?</p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/affiliate/apply')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/5"
              >
                Apply Now
              </Button>
              <button
                onClick={() => setShowStatusCheck(true)}
                className="text-[#D4AF37] hover:text-[#C4A030] text-sm"
              >
                Check Application Status
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleCheckStatus} className="bg-[#121212] border border-white/10 p-6">
            <h3 className="text-white font-medium mb-4">Check Application Status</h3>
            <div className="space-y-4">
              <Input
                type="email"
                value={statusEmail}
                onChange={(e) => setStatusEmail(e.target.value)}
                required
                className="bg-[#0a0a0a] border-white/10 text-white"
                placeholder="Email used in application"
              />
              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={checkingStatus}
                  className="flex-1 bg-[#D4AF37] text-black hover:bg-[#C4A030]"
                >
                  {checkingStatus ? (
                    <Spinner size={16} className="animate-spin" />
                  ) : (
                    'Check Status'
                  )}
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowStatusCheck(false)}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
